import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

export interface LegalDocument {
  id: string;
  slug: string;
  version: number;
  title: string;
  body_markdown: string;
  effective_date: string;
  is_active: boolean;
}

export function useActiveLegalDocuments() {
  return useQuery({
    queryKey: ["legal_documents", "active"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("legal_documents" as any)
        .select("*")
        .eq("is_active", true)
        .order("slug");
      if (error) throw error;
      return ((data || []) as unknown) as LegalDocument[];
    },
  });
}

export function useMyAcceptances() {
  const { user } = useAuth();
  return useQuery({
    queryKey: ["legal_acceptances", user?.id],
    enabled: !!user,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("legal_acceptances" as any)
        .select("*")
        .eq("user_id", user!.id);
      if (error) throw error;
      return ((data || []) as unknown) as Array<{ document_slug: string; document_version: number; accepted_at: string }>;
    },
  });
}

export function useAcceptDocuments() {
  const { user } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (params: { docs: LegalDocument[]; typed_signature: string }) => {
      const ua = typeof navigator !== "undefined" ? navigator.userAgent : null;
      const rows = params.docs.map((d) => ({
        user_id: user!.id,
        document_slug: d.slug,
        document_version: d.version,
        typed_signature: params.typed_signature,
        user_agent: ua,
      }));
      const { error } = await supabase.from("legal_acceptances" as any).insert(rows);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["legal_acceptances"] }),
  });
}
